<template>
  <o-modal
    ref="modal"
    :loading="processing"
    :portal="portal"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('components.order.actions.addPayment.pay', { amount: amountLabel }), type: 'confirm', disabled: isDisabled, callback: handlePay },
    ]"
    modal-class="m-2 bg-white dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-2xl sm:w-2xl h-full max-h-lg bg-gray-50"
    content-class="mx-auto max-w-sm h-full py-4 px-4 flex flex-col"
    sidebar-class="hidden sm:block w-1/3 px-5 py-5 text-white dark:border-l dark:border-gray-800"
    slot-class="h-full mb-4"
  >
    <template #sidebar>
      <div class="hidden sm:flex h-full flex-col justify-between max-h-screen">
        <div>
          <o-icon icon="payment" :size="32" class="block pb-3 opacity-75" />
          <h4 class="font-medium mb-2px">
            {{ $t('order.addPayment') }}
          </h4>
          <p class="text-sm opacity-75">
            {{ $t('components.order.actions.addPayment.newCustomerPayment') }}
          </p>
        </div>
        <div>
          <order-summary :order="order" class="opacity-85" />
        </div>
      </div>
    </template>

    <div class="flex flex-col h-full">
      <div class="mb-8 bg-white dark:bg-gray-900/30 w-full rounded-md overflow-hidden">
        <div class="w-full flex items-center border" :class="hasChange ? 'rounded-t-md' : 'rounded-md'">
          <p class="block px-4 mr-2 border-r text-xl tracking-tight text-gray-500 dark:text-gray-400 font-light rounded-md">
            {{ currencySymbol }}
          </p>
          <input v-model="amount" type="text" class="flex-grow text-center w-full bg-transparent p-4 text-xl tracking-tight">
        </div>
        <div v-if="hasChange" class="w-full border border-t-0 px-4 py-1 rounded-b-md">
          <div class="text-sm">
            <div class="py-2 flex items-center space-x-3">
              <o-icon icon="warning" class="inline-block text-yellow-600 bg-yellow-200/50 p-1 rounded-lg" />
              <div class="leading-tight">
                <p class="font-medium">
                  {{ $t('components.order.actions.addPayment.changeDue', { change: changeLabel }) }}
                </p>
                <p class="opacity-70">
                  {{ $t('components.order.actions.addPayment.paymentAmountMoreThanRemainder') }}
                </p>
              </div>
            </div>
            <checkbox-field v-if="!hasTip && !isTipTypePerEmployee" v-model="tipEnabled" :label="$t('components.order.actions.addPayment.saveChangeAsTip')" class="ml-1 pt-2 mt2 border-t" />
          </div>
        </div>
      </div>
      <div class="-mx-1 h-10 flex-grow flex-shrink">
        <div class="h-full border-b border-t">
          <o-scroll>
            <div class="pt-6">
              <payment-controller
                ref="controller"
                v-model:selected="selected"
                v-model:processing="processing"
                :amount="amount"
                :order="order"
                :tip="tip"
              />
            </div>
          </o-scroll>
        </div>
      </div>
      <div v-if="!hasAvailableFunds" class="mt-3">
        <o-alert show variant="warning" :title="$t('components.order.actions.addPayment.insufficientFunds', { label: selected.label })" class="text-sm">
          <p v-html="$t('components.order.actions.addPayment.paymentButBalance', { amount: amountLabel, label: selected.label, balance: $currency.format(selected.balance) })" />

          <o-button variant="warning" size="sm" class="mt-3" @click="amount = selected.balance">
            {{ $t('components.order.actions.addPayment.changePayment', { balance: $currency.format(selected.balance) }) }}
          </o-button>
        </o-alert>
      </div>

      <div v-if="!hasRemainder" class="pt-3 flex items-center mb-4">
        <toggle-field v-model="closeEnabled" class="mr-3" />

        <div>
          <p class="opacity-80 text-sm font-medium">
            {{ $t('order.close') }}
          </p>
          <p class="opacity-60 text-sm">
            {{ $t('components.order.actions.addPayment.saleFinalised') }}
          </p>
        </div>
      </div>
    </div>

    <o-dialog
      ref="changeDialog"
      :title="$t('components.order.actions.addPayment.changeDue', { change: changeLabel })"
      :options="[
        { text: $t('components.order.actions.addPayment.keepAsOverpayment'), variant: 'warning', type: 'confirm', response: 'overcharge' },
        { text: $t('components.order.actions.addPayment.returnChange'), type: 'confirm', variant: 'success', response: 'change' },
      ]"
    >
      <div class="pt-3">
        <div class="flex items-center justify-between mb-2">
          <p class="text-sm opacity-60">
            {{ $t('components.order.actions.addPayment.customerPaid') }}
          </p>
          <p class="font-semibold opacity-80">
            {{ amountLabel }}
          </p>
        </div>
        <div class="flex items-center justify-between mb-2 border-b pb-2">
          <p class="text-sm opacity-60">
            {{ $t('components.order.actions.addPayment.remainingBalance') }}
          </p>
          <p class="font-semibold opacity-80">
            {{ remainderLabel }}
          </p>
        </div>
        <div class="flex items-center justify-between">
          <p class="text-sm opacity-60">
            {{ $t('components.order.actions.addPayment.changeRequired') }}
          </p>
          <p class="font-semibold opacity-80">
            {{ changeLabel }}
          </p>
        </div>
      </div>
    </o-dialog>

    <template #loader>
      <div class="flex flex-col items-center">
        <o-loader />
        <div class="text-sm opacity-70 mt-6 uppercase tracking-wider">
          {{ $t('components.order.actions.addPayment.processingPayment') }}
        </div>
      </div>
    </template>
  </o-modal>
</template>

<script setup lang="ts">
import PaymentController from '@/components/order/elements/payments/PaymentController.vue'
import OrderSummary from '@/components/order/elements/Summary.vue'
import {
  OAlert,
} from '@/components/elements'
import {
  CheckboxField,
  ToggleField,
} from '@/components/fields'

interface Props {
  order: Record<string, any>
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'update:order', value: any): void
  (e: 'close'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()

const { $notification, $bus, $currency, $auth } = useNuxtApp()
const { t } = useI18n()

const modal = ref<any>()
const controller = ref<any>()
const changeDialog = ref<any>()

const selected = ref<any>(null)
const amount = ref<number | string>(0)
const processing = ref(false)
const tipEnabled = ref(false)
const closeEnabled = ref(true)

const remainder = computed(() => {
  return Number(props.order.data.totals.remainder.toFixed(2))
})

const tip = computed(() => {
  if (tipEnabled.value) {
    const amountVal = amount.value as number
    return amountVal - remainder.value
  }

  return null
})

const change = computed(() => {
  const amountVal = amount.value as number
  return amountVal - remainder.value
})

const currencySymbol = computed(() => {
  return ($auth as any).info.location.currencySymbol
})

const changeLabel = computed(() => {
  const amountVal = amount.value as number
  return $currency.format(amountVal - remainder.value)
})

const amountLabel = computed(() => {
  return $currency.format(amount.value)
})

const remainderLabel = computed(() => {
  return $currency.format(remainder.value)
})

const hasChange = computed(() => {
  const amountVal = amount.value as number
  return remainder.value > 0 && remainder.value < amountVal
})

const hasAvailableFunds = computed(() => {
  const selectedVal = selected.value
  const amountVal = amount.value as number
  return !selectedVal?.data?.balance || Number(selectedVal?.data?.balance) >= amountVal
})

const hasRemainder = computed(() => {
  return (remainder.value - (amount.value as number)) > 0
})

const hasTip = computed(() => {
  return props.order.hasTip
})

const isTipTypePerEmployee = computed(() => {
  return props.order.isTipTypePerEmployee
})

const isDisabled = computed(() => {
  const amountVal = amount.value as number
  return processing.value || amountVal <= 0 || !selected.value || !hasAvailableFunds.value
})

function open () {
  amount.value = Math.max(remainder.value, 0)
  processing.value = false

  modal.value.open()
}

async function addPayment () {
  /* create payment object then submit payment object to api */
  try {
    processing.value = true

    const order = await controller.value.process()

    if (order) {
      emit('update:order', order)
      $bus.emit.updated('Order', order)

      $notification.toast({
        title: t('components.order.actions.addPayment.success'),
        text: t('components.order.actions.addPayment.successInfo', { amount: amountLabel.value, label: selected.value.label }),
        variant: 'success',
      })

      if (closeEnabled.value && !hasRemainder.value) {
        emit('close')
      }

      controller.value.refresh()

      processing.value = false

      return true
    }

    processing.value = false

    return false
  } catch (e: any) {
    $notification.error({
      title: 'Failed to add payment',
      errors: e?.errors,
    })

    processing.value = false

    return false
  }
}

async function handlePay () {
  if (hasChange.value && !tipEnabled.value && selected.value.change) {
    const response = await changeDialog.value.open()

    if (response === 'change') {
      amount.value = remainder.value
    } else if (response === 'cancel') {
      return false
    }
  }

  processing.value = true

  await new Promise(resolve => setTimeout(resolve, 500))

  return await handleConfirm()
}

async function handleConfirm () {
  const success = await addPayment()

  if (success) {
    if (hasRemainder.value) {
      amount.value = Math.max(remainder.value, 0)
      processing.value = false
      selected.value = null

      return false
    }
  }

  return success
}

defineExpose({ open })
</script>
